import React from 'react';
import { useForm } from 'react-hook-form';
import { CreatePersonaPayload } from '../../hooks/usePersonaAdmin';

interface Props {
  onClose: () => void;
  onSubmit: (payload: CreatePersonaPayload) => Promise<void> | void;
  isSubmitting?: boolean;
}

interface FormValues {
  name: string;
  display_name: string;
  listener_role_id: string;
  welcome_message: string;
}

const CreatePersonaDialog: React.FC<Props> = ({ onClose, onSubmit, isSubmitting }) => {
  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<FormValues>({
    mode: 'onChange',
    defaultValues: { name: '', display_name: '', listener_role_id: '', welcome_message: '' },
  });

  const submit = handleSubmit(async (values) => {
    const roleId = Number.parseInt(values.listener_role_id, 10);
    if (Number.isNaN(roleId)) return;
    await onSubmit({
      name: values.name.trim(),
      display_name: values.display_name.trim(),
      listener_role_id: roleId,
      welcome_message: values.welcome_message?.trim() || undefined,
    });
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="w-full max-w-lg rounded-lg bg-white p-6 shadow-xl">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">Create Persona</h2>
          <button onClick={onClose} className="text-2xl leading-none text-gray-400 hover:text-gray-600">
            &times;
          </button>
        </div>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label htmlFor="persona-name" className="mb-1 block text-sm font-medium text-gray-700">
              Name (internal)
            </label>
            <input
              id="persona-name"
              className="w-full rounded border border-gray-300 p-2 focus:border-transparent focus:ring-2 focus:ring-blue-500"
              placeholder="pathfinder"
              {...register('name', { required: 'Name is required' })}
              data-testid="persona-form-name"
            />
            {errors.name && <p className="mt-1 text-xs text-red-600">{errors.name.message}</p>}
          </div>
          <div>
            <label htmlFor="persona-display-name" className="mb-1 block text-sm font-medium text-gray-700">
              Display Name
            </label>
            <input
              id="persona-display-name"
              className="w-full rounded border border-gray-300 p-2 focus:border-transparent focus:ring-2 focus:ring-blue-500"
              placeholder="Pathfinder"
              {...register('display_name', { required: 'Display name is required' })}
              data-testid="persona-form-display-name"
            />
            {errors.display_name && <p className="mt-1 text-xs text-red-600">{errors.display_name.message}</p>}
          </div>
          <div>
            <label htmlFor="persona-listener-role" className="mb-1 block text-sm font-medium text-gray-700">
              Listener Role ID
            </label>
            <input
              id="persona-listener-role"
              type="number"
              className="w-full rounded border border-gray-300 p-2 focus:border-transparent focus:ring-2 focus:ring-blue-500"
              placeholder="123"
              {...register('listener_role_id', {
                required: 'Listener role id is required',
                validate: (v) => (Number.isInteger(Number.parseInt(v, 10)) ? true : 'Must be a number'),
              })}
              data-testid="persona-form-listener-role"
            />
            {errors.listener_role_id && <p className="mt-1 text-xs text-red-600">{errors.listener_role_id.message}</p>}
          </div>
          <div>
            <label htmlFor="persona-welcome" className="mb-1 block text-sm font-medium text-gray-700">
              Welcome Message <span className="text-xs text-gray-400">(optional)</span>
            </label>
            <textarea
              id="persona-welcome"
              rows={3}
              className="w-full rounded border border-gray-300 p-2 focus:border-transparent focus:ring-2 focus:ring-blue-500"
              placeholder="Hi — glad you're here…"
              {...register('welcome_message')}
              data-testid="persona-form-welcome"
            />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!isValid || isSubmitting}
              className="bg-blue-primary rounded px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {isSubmitting ? 'Creating…' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreatePersonaDialog;
